import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Users, Calendar, Mail, FileSpreadsheet, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function LegerHistoryModal({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/leger/history")
      .then(({ data }) => setItems(data.items))
      .catch(() => toast.error("Gagal memuat riwayat"))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="leger-history-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <History className="w-5 h-5 text-blue-700" /> Riwayat Impor Leger
          </DialogTitle>
          <DialogDescription>
            Catatan audit setiap impor Leger — siapa yang mengunggah, kapan, semester berapa, dan siswa mana yang datanya diperbarui.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {loading ? (
            <div className="grid place-items-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500" data-testid="history-empty">
              Belum ada riwayat impor Leger.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((h) => {
                const isOpen = expanded === h.id;
                return (
                  <div key={h.id} className="border border-slate-200 rounded-lg bg-white" data-testid={`history-row-${h.id}`}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : h.id)}
                      className="w-full flex flex-col sm:flex-row sm:items-center gap-2 p-3 hover:bg-slate-50 text-left transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          <FileSpreadsheet className="w-4 h-4 text-blue-700 flex-shrink-0" />
                          <span className="truncate">{h.filename}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-blue-100 text-blue-800">
                            Semester {h.semester}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(h.timestamp).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{h.admin_name || h.admin_email}</span>
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{h.students_touched} siswa • {h.rows_processed} baris</span>
                        </div>
                      </div>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {isOpen && h.students?.length > 0 && (
                      <div className="border-t border-slate-100 bg-slate-50/50 p-3 max-h-56 overflow-y-auto" data-testid={`history-students-${h.id}`}>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
                          Siswa Terpengaruh ({h.students.length})
                        </div>
                        <div className="grid sm:grid-cols-2 gap-1.5 text-xs">
                          {h.students.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 py-1 px-2 bg-white rounded border border-slate-100">
                              <span className="font-mono text-slate-500 text-[10px]">{s.nisn}</span>
                              <span className="truncate text-slate-800">{s.nama}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
